import { Suspense, lazy } from "react";
import { Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CookieConsent } from "./components/shared/CookieConsent";
import { ScrollToTop } from "./components/shared/ScrollToTop";
import { Loader2 } from "lucide-react";

 // Route Guards for RBAC
 import { AdminRoute } from "./components/auth/AdminRouteGuard";
 
 // Dealer Route Guards (Phase 3 RBAC)
 import {
   RequireDealerMember,
   RequireDealerAdmin,
 } from "./components/auth/DealerRouteGuards";

// Access Guards for Buyers + Subscribers (email verification, subscription)
import { RequireVerifiedEmail, RequireSubscriber } from "./components/auth/AccessGuards";

// Geo-blocking guard for defense-in-depth
import { GeoBlockGuard } from "./components/auth/GeoBlockGuard";

// Error boundary for admin routes - catches useAuth errors and shows friendly fallback
import { AdminErrorBoundary } from "./components/admin/AdminErrorBoundary";

// Critical path pages - loaded eagerly for best FCP
import HomePage from "./pages/HomePage";
import NotFound from "./pages/NotFound";

// Lazy load all other pages for code splitting
const NewsPage = lazy(() => import("./pages/NewsPage"));
const VehicleReviewsPage = lazy(() => import("./pages/VehicleReviewsPage"));
const VehicleReviewDetailPage = lazy(() => import("./pages/VehicleReviewDetailPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const EventDetailPage = lazy(() => import("./pages/EventDetailPage"));
const VideosPage = lazy(() => import("./pages/VideosPage"));
const FeaturedPage = lazy(() => import("./pages/FeaturedPage"));
const ArticleDetailPage = lazy(() => import("./pages/ArticleDetailPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const AdvertisePage = lazy(() => import("./pages/AdvertisePage"));
const IndustryInsightsPage = lazy(() => import("./pages/IndustryInsightsPage"));
const SubscriberDashboardPage = lazy(() => import("./pages/SubscriberDashboardPage"));
const UpgradePage = lazy(() => import("./pages/UpgradePage"));

// Deal Builder
const DealBuilderPage = lazy(() => import("./pages/DealBuilderPage"));
const DealBuilderLandingPage = lazy(() => import("./pages/DealBuilderLandingPage"));
const DealDetailPage = lazy(() => import("./pages/DealDetailPage"));
const DealerRegistrationPage = lazy(() => import("./pages/DealerRegistrationPage"));
const DealersPage = lazy(() => import("./pages/DealersPage"));
const DealIQDemoPage = lazy(() => import("./pages/DealIQDemoPage"));

// Buyer Auth & Account
const BuyerAuthPage = lazy(() => import("./pages/BuyerAuthPage"));
const VerificationSentPage = lazy(() => import("./pages/auth/VerificationSentPage"));
const VerifyEmailPage = lazy(() => import("./pages/auth/VerifyEmailPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const BuyerAccountPage = lazy(() => import("./pages/BuyerAccountPage"));

// Dealer Routes
const DealerAuthPage = lazy(() => import("./pages/dealer/DealerAuthPage"));
const DealerDashboard = lazy(() => import("./pages/dealer/DealerDashboard"));
const DealerLeadsPage = lazy(() => import("./pages/dealer/DealerLeadsPage"));
const DealerSettingsPage = lazy(() => import("./pages/dealer/DealerSettingsPage"));
const SalespersonDashboard = lazy(() => import("./pages/dealer/SalespersonDashboard"));
const DealerSetupPage = lazy(() => import("./pages/dealer/DealerSetupPage"));
const DealerAccountPage = lazy(() => import("./pages/dealer/DealerAccountPage"));
const AcceptInvitePage = lazy(() => import("./pages/dealer/AcceptInvitePage"));
const DealerTeamPage = lazy(() => import("./pages/dealer/DealerTeamPage"));
const DealerLeadDetailPage = lazy(() => import("./pages/dealer/DealerLeadDetailPage"));

// Admin Routes
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminArticleEdit = lazy(() => import("./pages/admin/AdminArticleEdit"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminVehicleReviews = lazy(() => import("./pages/admin/AdminVehicleReviews"));
const AdminVehicleReviewEdit = lazy(() => import("./pages/admin/AdminVehicleReviewEdit"));
const AdminReviewGenerator = lazy(() => import("./pages/admin/AdminReviewGenerator"));
const AdminVehicleTrims = lazy(() => import("./pages/admin/AdminVehicleTrims"));
const AdminBuyerAccounts = lazy(() => import("./pages/admin/AdminBuyerAccounts"));
const AdminDealerships = lazy(() => import("./pages/admin/AdminDealerships"));
const AdminDealerAccounts = lazy(() => import("./pages/admin/AdminDealerAccounts"));
const AdminDealershipDetail = lazy(() => import("./pages/admin/AdminDealershipDetail"));
const AdminDeals = lazy(() => import("./pages/admin/AdminDeals"));
const AdminTaxonomy = lazy(() => import("./pages/admin/AdminTaxonomy"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers"));
const AdminIntegrations = lazy(() => import("./pages/admin/AdminIntegrations"));
const AdminAdvertising = lazy(() => import("./pages/admin/AdminAdvertising"));
const AdminNewsSources = lazy(() => import("./pages/admin/AdminNewsSources"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminContactSubmissions = lazy(() => import("./pages/admin/AdminContactSubmissions"));
const AdminSmokeTest = lazy(() => import("./pages/admin/AdminSmokeTest"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// App component - receives QueryClient and Auth from main.tsx
const App = () => (
  <ThemeProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
          <ScrollToTop />
          <CookieConsent />
          {/* GeoBlockGuard - defense-in-depth blocking for high-risk countries */}
          <GeoBlockGuard>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ==================== PUBLIC ROUTES ==================== */}
              <Route path="/" element={<HomePage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:slug" element={<ArticleDetailPage />} />
              <Route path="/reviews" element={<VehicleReviewsPage />} />
              <Route path="/reviews/:slug" element={<VehicleReviewDetailPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:slug" element={<EventDetailPage />} />
              <Route path="/videos" element={<VideosPage />} />
              <Route path="/featured" element={<FeaturedPage />} />
              <Route path="/deals/builder" element={<DealBuilderLandingPage />} />
              <Route path="/deals/builder/create" element={
                <RequireVerifiedEmail>
                  <DealBuilderPage />
                </RequireVerifiedEmail>
              } />
              <Route path="/deals/shared/:shareToken" element={<DealDetailPage />} />
              <Route path="/dealers" element={<DealersPage />} />
              <Route path="/dealers/register" element={<DealerRegistrationPage />} />
              <Route path="/deal-iq-demo" element={<DealIQDemoPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/advertise" element={<AdvertisePage />} />
              {/* Industry Insights - public teaser page */}
              <Route path="/insights" element={<IndustryInsightsPage />} />
              {/* Upgrade page - public, redirects if already subscribed */}
              <Route path="/upgrade" element={<UpgradePage />} />
              {/* Insights Dashboard - requires subscriber */}
              <Route path="/insights/dashboard" element={
                <RequireSubscriber>
                  <SubscriberDashboardPage />
                </RequireSubscriber>
              } />
              {/* ==================== BUYER AUTH ROUTES ==================== */}
              {/* Auth pages - public (redirect if already logged in handled in components) */}
              <Route path="/auth/buyer" element={<BuyerAuthPage />} />
              <Route path="/auth/verification-sent" element={<VerificationSentPage />} />
              <Route path="/auth/verify" element={<VerifyEmailPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              
              {/* Buyer-only protected route - requires verified email */}
              <Route path="/account" element={
                <RequireVerifiedEmail>
                  <BuyerAccountPage />
                </RequireVerifiedEmail>
              } />

              {/* ==================== DEALER ROUTES ==================== */}
              {/* Dealer auth pages - public */}
              <Route path="/dealer/login" element={<DealerAuthPage />} />
              <Route path="/dealers/auth" element={<Navigate to="/dealer/login" replace />} />
              <Route path="/dealers/setup" element={<DealerSetupPage />} />
              <Route path="/dealer/accept-invite" element={<AcceptInvitePage />} />
              
              {/* Dealer application - canonical URL with redirect */}
              <Route path="/dealer/apply" element={<DealerRegistrationPage />} />
              <Route path="/dealers/register" element={<Navigate to="/dealer/apply" replace />} />
              
               {/* Dealer Member routes (admin + sales) */}
              <Route path="/dealer/dashboard" element={
                 <RequireDealerMember>
                  <DealerDashboard />
                 </RequireDealerMember>
              } />
              <Route path="/dealer/leads" element={
                 <RequireDealerMember>
                  <DealerLeadsPage />
                 </RequireDealerMember>
               } />
               <Route path="/dealer/leads/:leadId" element={
                 <RequireDealerMember>
                  <DealerLeadDetailPage />
                 </RequireDealerMember>
               } />
               
               {/* Dealer Admin only routes */}
              <Route path="/dealer/team" element={
                 <RequireDealerAdmin>
                  <DealerTeamPage />
                 </RequireDealerAdmin>
              } />
              <Route path="/dealer/settings" element={
                 <RequireDealerAdmin>
                  <DealerSettingsPage />
                 </RequireDealerAdmin>
              } />
              <Route path="/dealer/account" element={
                 <RequireDealerAdmin>
                  <DealerAccountPage />
                 </RequireDealerAdmin>
              } />
              
              {/* Salesperson only route */}
              <Route path="/dealer/sales" element={
                 <RequireDealerMember>
                  <SalespersonDashboard />
                 </RequireDealerMember>
              } />

              {/* ==================== ADMIN ROUTES ==================== */}
              {/* Admin login - public */}
              <Route path="/admin/login" element={<AdminLogin />} />
              
              {/* Admin-only protected routes - wrapped in AdminErrorBoundary */}
              <Route path="/admin" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/articles" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminArticles />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/articles/:id" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminArticleEdit />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/vehicle-reviews" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminVehicleReviews />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/vehicle-reviews/generate" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminReviewGenerator />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/vehicle-reviews/:id" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminVehicleReviewEdit />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/events" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminEvents />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/vehicle-trims" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminVehicleTrims />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/buyer-accounts" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminBuyerAccounts />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/dealerships" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminDealerships />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/dealer-accounts" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminDealerAccounts />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/dealer-accounts/:id" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminDealershipDetail />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/deals" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminDeals />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/taxonomy" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminTaxonomy />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/subscribers" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminSubscribers />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/integrations" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminIntegrations />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/advertising" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminAdvertising />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/news-sources" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminNewsSources />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/audit-logs" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminAuditLogs />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/analytics" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminAnalytics />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/contact-submissions" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminContactSubmissions />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/ops/smoke" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminSmokeTest />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              <Route path="/admin/subscriptions" element={
                <AdminErrorBoundary>
                  <AdminRoute>
                    <AdminSubscriptions />
                  </AdminRoute>
                </AdminErrorBoundary>
              } />
              
              {/* ==================== FALLBACK ==================== */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </GeoBlockGuard>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
);

export default App;
